import { Injectable, NgZone } from '@angular/core';

import {
  Subscription,
  fromEvent,
  merge,
  timer
} from 'rxjs';

import {
  startWith,
  switchMap,
  throttleTime
} from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import { PopupService } from '../../shared/services/popup.service';
import { APP_MESSAGES } from '../../shared/constants/app-messages.constants';

const APP_TEXT = APP_MESSAGES.APP;

/*
 * A user is logged out after this much continuous inactivity,
 * regardless of how long ago they logged in. Any tracked activity
 * event resets the countdown, so an active user is never logged
 * out by this timer no matter how long the session runs.
 */
const IDLE_TIMEOUT_MS = 6 * 60 * 60 * 1000;

/*
 * Resetting the countdown on every single event (mousemove fires
 * continuously) is unnecessary work over a 6 hour window, so
 * activity is sampled at most this often.
 */
const ACTIVITY_SAMPLE_MS = 30 * 1000;

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
  'click'
];

@Injectable({
  providedIn: 'root'
})
export class IdleTimeoutService {

  private started = false;
  private promptOpen = false;
  private subscription: Subscription | null = null;

  constructor(
    private ngZone: NgZone,
    private authService: AuthService,
    private popup: PopupService
  ) {}

  /*
   * Safe to call more than once (e.g. on every login); only the
   * first call actually attaches the activity listeners. The
   * listeners stay attached for the lifetime of the app and simply
   * do nothing while the user is signed out.
   */
  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;

    this.ngZone.runOutsideAngular(() => {
      const activity$ = merge(
        ...ACTIVITY_EVENTS.map(eventName =>
          fromEvent(document, eventName, { passive: true })
        )
      ).pipe(
        throttleTime(ACTIVITY_SAMPLE_MS, undefined, {
          leading: true,
          trailing: true
        }),
        startWith(0)
      );

      this.subscription = activity$
        .pipe(
          switchMap(() => timer(IDLE_TIMEOUT_MS))
        )
        .subscribe(() => {
          this.ngZone.run(() => this.onIdleTimeout());
        });
    });
  }

  private onIdleTimeout(): void {
    if (
      this.promptOpen ||
      !this.authService.isAuthenticated()
    ) {
      return;
    }

    this.promptOpen = true;

    void this.popup
      .warning(
        APP_TEXT.SESSION_TIMEOUT.message,
        APP_TEXT.SESSION_TIMEOUT.title
      )
      .then(() => {
        this.promptOpen = false;
        this.authService.logout(true);
      });
  }
}
